import { useCallback, useEffect, useState } from 'react';
import { claudeConnectApi, staffAuthApi } from '../lib/api';
import { parseHash } from '../lib/sites';
import { getStaffToken, setStaffToken, clearStaffToken } from '../lib/staffSession';
import { SignInForm } from '../hooks/useStaffHub';

// Connect Claude (staff.silverservice.com/#connect-claude?request=…): where a
// staff member lets Claude read Silver Service as them. Claude's sign-in
// (backend/lib/claudeConnectorAuth.js) parks its request and sends the browser
// here; Allow or Cancel sends it back to Claude. Uses the start page's own
// sign-in, so someone already signed in only has to answer.
export default function ConnectClaude() {
  const [requestId] = useState(() => parseHash().params.get('request') || '');
  const [request, setRequest] = useState(null);
  const [token, setToken] = useState(() => getStaffToken('staff'));
  const [me, setMe] = useState(null);
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Connect Claude · Silver Service';
    return () => { document.title = previous; };
  }, []);

  useEffect(() => {
    if (!requestId) { setProblem('This link is missing its request. Go back to Claude and connect again.'); return; }
    claudeConnectApi.request(requestId)
      .then(setRequest)
      .catch((e) => setProblem(e.message || 'This request has expired. Go back to Claude and connect again.'));
  }, [requestId]);

  const forget = useCallback(() => {
    clearStaffToken('staff');
    setToken('');
    setMe(null);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    staffAuthApi.session(token)
      .then((m) => { if (!cancelled) setMe(m); })
      .catch((e) => { if (!cancelled && e.status === 401) forget(); });
    return () => { cancelled = true; };
  }, [token, forget]);

  function signedIn(t, admin) {
    setStaffToken('staff', t);
    setMe(admin);
    setToken(t);
  }

  async function answer(allow) {
    setBusy(true);
    try {
      const { redirect } = allow
        ? await claudeConnectApi.approve(token, requestId)
        : await claudeConnectApi.deny(requestId);
      window.location.replace(redirect);
    } catch (e) {
      setBusy(false);
      if (e.status === 401) forget();
      else setProblem(e.message || 'Something went wrong. Go back to Claude and try again.');
    }
  }

  let body;
  if (problem) {
    body = (
      <div style={s.box}>
        <img src="/logo.png" alt="Silver Service" style={s.logo} />
        <p style={s.problem}>{problem}</p>
      </div>
    );
  } else if (!request) {
    body = <p style={s.quiet}>Loading…</p>;
  } else if (!token) {
    body = <SignInForm heading={`Sign in to connect ${request.destination}`} onSignedIn={signedIn} />;
  } else if (!me) {
    body = <p style={s.quiet}>Loading…</p>;
  } else {
    body = (
      <div style={s.box}>
        <img src="/logo.png" alt="Silver Service" style={s.logo} />
        <h1 style={s.heading}>Connect {request.destination} to Silver Service?</h1>
        <p style={s.text}>
          {request.destination} will be able to look things up in Silver Service as you,
          {' '}<strong>{me.name || me.email}</strong> ({me.permission}).
        </p>
        <ul style={s.list}>
          <li>It can read what your login can see, starting with customers.</li>
          <li>It can't change anything: no orders, prices or customer records.</li>
          <li>To disconnect it later, remove the connector in Claude's settings.</li>
        </ul>
        <div style={s.buttons}>
          <button type="button" style={s.primary} disabled={busy} onClick={() => answer(true)}>
            {busy ? 'Connecting…' : 'Allow'}
          </button>
          <button type="button" style={s.outline} disabled={busy} onClick={() => answer(false)}>Cancel</button>
        </div>
        <button type="button" style={s.link} disabled={busy} onClick={forget}>Not you? Use a different login</button>
      </div>
    );
  }

  return <div style={s.center}>{body}</div>;
}

const RED = '#A50B18';
const s = {
  center: { minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' },
  box: { background: '#fff', borderRadius: 12, padding: '2rem', maxWidth: 440, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: 14, boxSizing: 'border-box' },
  logo: { height: 40, alignSelf: 'center' },
  heading: { color: '#222', fontWeight: 700, fontSize: '1.15rem', textAlign: 'center', margin: 0 },
  text: { color: '#333', fontSize: '0.95rem', lineHeight: 1.5, margin: 0 },
  list: { color: '#555', fontSize: '0.9rem', lineHeight: 1.5, margin: 0, paddingLeft: 20 },
  buttons: { display: 'flex', gap: 10, marginTop: 4 },
  primary: { flex: 1, background: RED, color: '#fff', border: 'none', borderRadius: 8, padding: '0.7rem 1.4rem', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  outline: { flex: 1, background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '0.7rem 1.4rem', fontSize: '0.95rem', cursor: 'pointer' },
  link: { background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer', alignSelf: 'center', padding: 0 },
  quiet: { color: '#888', fontSize: '0.95rem', margin: 0 },
  problem: { color: '#444', fontSize: '0.92rem', margin: 0, lineHeight: 1.5, textAlign: 'center' },
};
